/**
 * PlanDrawer — criar / editar plano (com composição do kit).
 *
 * Props:
 *   isOpen, onClose
 *   productId
 *   plan                — plano a editar (undefined no create)
 *   physicalProducts    — lista do catálogo (pra dropdown da composição)
 *   onSaved(plan)
 */

import React, { useEffect, useState, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { plansV2API } from '../../../services/api/v2/plans';
import { centsToDisplay, parseInputToCents } from '../../../utils/currencyMask';
import Dropdown from '../../ui/Dropdown';
import Drawer from './Drawer';

const PAYMENT_OPTIONS = [
  { id: 'credit_card', label: 'Cartão', icon: 'mdi:credit-card-outline' },
  { id: 'pix',         label: 'PIX',     icon: 'mdi:qrcode' },
  { id: 'boleto',      label: 'Boleto',  icon: 'mdi:barcode' },
];

const EMPTY = {
  name: '',
  description: '',
  price_cents: 0,
  billing_type: 'single',
  installments_max: 12,
  is_active: true,
  payment_methods: ['credit_card', 'pix', 'boleto'],
  items: [], // { physical_product_id, quantity }
};

// IMPORTANTE: valores no banco estão em CENTAVOS. fmtBRL divide por 100
// pra exibir em reais. Não passar reais aqui — passa centavos sempre.
const fmtBRL = (cents) =>
  (Number(cents || 0) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const PlanDrawer = ({ isOpen, onClose, productId, plan, physicalProducts = [], onSaved }) => {
  const isEdit = !!plan?.id;
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [topError, setTopError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setTopError('');
    if (plan) {
      setForm({
        name: plan.name || '',
        description: plan.description || '',
        price_cents: Number(plan.price_cents || 0),
        billing_type: plan.billing_type || 'single',
        installments_max: Number(plan.installments_max || 12),
        is_active: plan.is_active !== false,
        payment_methods: Array.isArray(plan.payment_methods) && plan.payment_methods.length
          ? plan.payment_methods.filter((m) => PAYMENT_OPTIONS.find((o) => o.id === m))
          : ['credit_card', 'pix', 'boleto'],
        items: (plan.items || []).map((it) => ({
          physical_product_id: it.physical_product_id,
          quantity: Number(it.quantity || 1),
        })),
      });
    } else {
      // default no create: já entra com 1 item vazio (se há catálogo)
      const first = physicalProducts[0];
      setForm({
        ...EMPTY,
        items: first ? [{ physical_product_id: first.id, quantity: 1 }] : [],
      });
    }
  }, [isOpen, plan, physicalProducts]);

  const update = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
    setErrors((p) => ({ ...p, [field]: undefined }));
  };

  // Item helpers — physical_product_id único por plan_item (backend rejeita duplicata)
  const usedIds = useMemo(() => new Set(form.items.map((it) => it.physical_product_id)), [form.items]);
  const freePhysicals = physicalProducts.filter((pp) => !usedIds.has(pp.id));

  const addItem = () => {
    const next = physicalProducts.find((pp) => !usedIds.has(pp.id));
    if (!next) return;
    update('items', [...form.items, { physical_product_id: next.id, quantity: 1 }]);
  };

  const updateItem = (idx, field, value) => {
    if (field === 'physical_product_id') {
      // bloqueia duplicata
      const dup = form.items.some((it, i) => i !== idx && it.physical_product_id === value);
      if (dup) return;
    }
    update('items', form.items.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  };

  const removeItem = (idx) => {
    update('items', form.items.filter((_, i) => i !== idx));
  };

  const togglePayment = (id) => {
    const current = form.payment_methods;
    if (current.includes(id)) {
      if (current.length === 1) return; // pelo menos 1
      update('payment_methods', current.filter((m) => m !== id));
    } else {
      update('payment_methods', [...current, id]);
    }
  };

  // Subtotal vs preço
  const subtotal = useMemo(() => {
    return form.items.reduce((acc, it) => {
      const pp = physicalProducts.find((p) => p.id === it.physical_product_id);
      return acc + Number(pp?.price_cents || 0) * Number(it.quantity || 1);
    }, 0);
  }, [form.items, physicalProducts]);

  // Auto-sum: preço de venda = soma dos itens. Atualiza sempre que itens
  // mudam (incluindo quantidade). Caso de uso: produtor monta kit e o
  // preço aparece sozinho — não precisa digitar.
  useEffect(() => {
    if (subtotal !== form.price_cents) {
      setForm((p) => ({ ...p, price_cents: subtotal }));
      setErrors((p) => ({ ...p, price_cents: undefined }));
    }
  }, [subtotal]); // eslint-disable-line react-hooks/exhaustive-deps

  const margin = form.price_cents - subtotal;

  const validate = () => {
    const newErr = {};
    if (!form.name.trim()) newErr.name = 'Nome é obrigatório';
    if (!form.price_cents || form.price_cents <= 0) newErr.price_cents = 'Preço deve ser maior que zero';
    if (!form.items.length) newErr.items = 'Adicione pelo menos 1 item ao plano';
    else if (form.items.some((it) => !it.quantity || it.quantity < 1)) {
      newErr.items = 'Quantidade de cada item deve ser >= 1';
    }
    if (!form.payment_methods.length) newErr.payment_methods = 'Selecione pelo menos 1 forma de pagamento';
    setErrors(newErr);
    return Object.keys(newErr).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate() || submitting) return;
    try {
      setSubmitting(true);
      setTopError('');
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        price_cents: Number(form.price_cents),
        billing_type: form.billing_type,
        installments_max: Number(form.installments_max) || 1,
        is_active: !!form.is_active,
        payment_methods: form.payment_methods,
        items: form.items.map((it, idx) => ({
          physical_product_id: it.physical_product_id,
          quantity: Number(it.quantity),
          display_order: idx,
        })),
      };
      let res;
      if (isEdit) {
        res = await plansV2API.update(plan.id, payload);
      } else {
        res = await plansV2API.create({ ...payload, product_id: productId });
      }
      const saved = res.data?.plan || res.data?.data || res.data;
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      const data = err.response?.data || {};
      const details = Array.isArray(data.details) && data.details.length
        ? data.details.map((d) => `${d.field}: ${d.message}`).join(' · ')
        : '';
      setTopError(details || data.message || err.message || 'Erro ao salvar plano');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Editar plano' : 'Novo plano'}
      subtitle={isEdit ? form.name : 'Configure preço, pagamento e composição do kit'}
      icon="mdi:credit-card-multiple-outline"
      size="lg"
      onSubmit={handleSubmit}
      submitting={submitting}
      submitLabel={isEdit ? 'Salvar plano' : 'Criar plano'}
    >
      {topError && (
        <div className="drawer-alert drawer-alert--error">
          <Icon icon="mdi:alert-circle" width="16" />
          <div>{topError}</div>
        </div>
      )}

      {/* Identidade */}
      <div className="drawer-field">
        <span className="drawer-label drawer-label--required">Nome do plano</span>
        <input
          className="drawer-input"
          type="text"
          value={form.name}
          onChange={(e) => update('name', e.target.value)}
          placeholder="Ex: Kit Completo, Plano Anual..."
        />
        {errors.name && (
          <span className="drawer-error">
            <Icon icon="mdi:alert-circle-outline" width="12" /> {errors.name}
          </span>
        )}
      </div>

      <div className="drawer-field">
        <span className="drawer-label">Descrição (opcional)</span>
        <textarea
          className="drawer-textarea"
          value={form.description}
          onChange={(e) => update('description', e.target.value)}
          placeholder="Detalhes que aparecem pro cliente no checkout"
        />
      </div>

      {/* Composição (kit) */}
      <div className="drawer-field">
        <span className="drawer-label">Composição do plano</span>
        <span className="drawer-help">
          Selecione os itens do catálogo que compõem este plano. Cada item entra
          uma vez — use quantidade pra somar.
        </span>

        {form.items.length === 0 && (
          <div className="drawer-alert drawer-alert--info">
            <Icon icon="mdi:information-outline" width="16" />
            <div>
              {physicalProducts.length === 0
                ? 'Cadastre pelo menos um item no Catálogo antes de criar um plano.'
                : 'Adicione itens pra montar o plano.'}
            </div>
          </div>
        )}

        {form.items.map((it, idx) => {
          const pp = physicalProducts.find((p) => p.id === it.physical_product_id);
          const itemSubtotal = Number(pp?.price_cents || 0) * Number(it.quantity || 1);
          return (
            <div key={idx} className="drawer-kit-row">
              <div className="drawer-field">
                <span className="drawer-label">Item</span>
                <Dropdown
                  compact
                  placeholder="Selecione o item"
                  value={it.physical_product_id || ''}
                  onChange={(v) => updateItem(idx, 'physical_product_id', Number(v))}
                  options={physicalProducts.map((pp2) => {
                    const usedElsewhere = form.items.some(
                      (other, oIdx) => oIdx !== idx && other.physical_product_id === pp2.id
                    );
                    return {
                      value: pp2.id,
                      label: pp2.name || `Item #${pp2.id}`,
                      icon: pp2.product_type === 'digital' ? 'mdi:cloud-download' : 'mdi:cube-outline',
                      secondary: usedElsewhere ? 'já no plano' : null,
                      disabled: usedElsewhere,
                    };
                  })}
                />
                {pp && (
                  <span className="drawer-help">
                    Unitário: {fmtBRL(pp.price_cents)} · Subtotal: <b>{fmtBRL(itemSubtotal)}</b>
                  </span>
                )}
              </div>
              <div className="drawer-field">
                <span className="drawer-label">Qtd.</span>
                <input
                  className="drawer-input"
                  type="number"
                  min={1}
                  value={it.quantity}
                  onChange={(e) => updateItem(idx, 'quantity', parseInt(e.target.value, 10) || 1)}
                />
              </div>
              <button
                type="button"
                className="drawer-kit-remove"
                onClick={() => removeItem(idx)}
                title="Remover item"
                disabled={form.items.length === 1}
              >
                <Icon icon="mdi:trash-can-outline" width="14" />
              </button>
            </div>
          );
        })}

        <button
          type="button"
          className="drawer-btn"
          style={{ width: '100%', marginTop: 10, justifyContent: 'center' }}
          onClick={addItem}
          disabled={freePhysicals.length === 0}
          title={freePhysicals.length === 0 ? 'Todos os itens do catálogo já estão no plano' : ''}
        >
          <Icon icon="mdi:plus" width="14" />
          {freePhysicals.length === 0
            ? 'Todos os itens do catálogo já estão no plano'
            : 'Adicionar item ao plano'}
        </button>

        {errors.items && (
          <span className="drawer-error">
            <Icon icon="mdi:alert-circle-outline" width="12" /> {errors.items}
          </span>
        )}
      </div>

      {/* Preço */}
      <div className="drawer-field--row">
        <div className="drawer-field">
          <span className="drawer-label drawer-label--required">Preço de venda</span>
          <div className="drawer-input--with-suffix" style={{ position: 'relative' }}>
            <input
              className="drawer-input"
              type="text"
              inputMode="numeric"
              value={centsToDisplay(form.price_cents)}
              readOnly
              tabIndex={-1}
              placeholder="0,00"
              style={{
                paddingLeft: 36,
                background: '#f4f4f5',
                color: '#374151',
                cursor: 'not-allowed',
              }}
            />
            <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#6b7280', fontSize: 13, fontWeight: 600 }}>R$</span>
          </div>
          <span className="drawer-help">
            <Icon icon="mdi:calculator-variant" width="12" style={{ verticalAlign: 'middle', marginRight: 2 }} />
            Calculado automaticamente pela soma dos itens.
          </span>
          {errors.price_cents && (
            <span className="drawer-error">
              <Icon icon="mdi:alert-circle-outline" width="12" /> {errors.price_cents}
            </span>
          )}
        </div>
        <div className="drawer-field">
          <span className="drawer-label">Forma de cobrança</span>
          <Dropdown
            compact
            value={form.billing_type}
            onChange={(v) => update('billing_type', v)}
            options={[
              { value: 'single', label: 'Única', icon: 'mdi:cash' },
              { value: 'recurring', label: 'Recorrente', icon: 'mdi:refresh' },
            ]}
          />
        </div>
      </div>

      {/* Resumo subtotal x preço (visual margin) */}
      {form.items.length > 0 && (
        <div className="drawer-alert drawer-alert--info" style={{ flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
            <Icon icon="mdi:chart-pie-outline" width="16" />
            <b>Resumo financeiro</b>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <span>Subtotal dos itens</span>
            <span><b>{fmtBRL(subtotal)}</b></span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <span>Preço de venda</span>
            <span><b>{fmtBRL(form.price_cents)}</b></span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', paddingTop: 6, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
            <span>Margem (preço – subtotal)</span>
            <span style={{ color: margin > 0 ? '#10b981' : margin < 0 ? '#f59e0b' : 'inherit' }}>
              <b>{margin >= 0 ? '+' : '-'}{fmtBRL(Math.abs(margin))}</b>
            </span>
          </div>
        </div>
      )}

      {/* Formas de pagamento */}
      <div className="drawer-field">
        <span className="drawer-label drawer-label--required">Formas de pagamento</span>
        <span className="drawer-help">Pelo menos uma forma deve estar ativa.</span>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {PAYMENT_OPTIONS.map((p) => {
            const active = form.payment_methods.includes(p.id);
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => togglePayment(p.id)}
                className={`drawer-toggle-btn ${active ? 'is-active' : ''}`}
                style={{ flex: 1, minWidth: 110 }}
              >
                <Icon icon={p.icon} width="18" />
                <span className="drawer-toggle-btn--label">{p.label}</span>
              </button>
            );
          })}
        </div>
        {errors.payment_methods && (
          <span className="drawer-error">
            <Icon icon="mdi:alert-circle-outline" width="12" /> {errors.payment_methods}
          </span>
        )}
      </div>

      {/* Parcelas */}
      <div className="drawer-field">
        <span className="drawer-label">Parcelas máximas (cartão)</span>
        <input
          className="drawer-input"
          type="number"
          min={1}
          max={12}
          value={form.installments_max}
          onChange={(e) => update('installments_max', Math.min(12, Math.max(1, parseInt(e.target.value, 10) || 1)))}
        />
        <span className="drawer-help">Mín: 1 · Máx: 12</span>
      </div>

      {/* Switch ativo */}
      <div className="drawer-switch" style={{ marginTop: 4 }}>
        <div className="drawer-switch__label">
          <span className="drawer-switch__label-title">Plano ativo</span>
          <span className="drawer-switch__label-desc">Quando desativado, não aparece em checkout/loja.</span>
        </div>
        <button
          type="button"
          className={`drawer-switch__input ${form.is_active ? 'is-on' : ''}`}
          onClick={() => update('is_active', !form.is_active)}
          aria-pressed={form.is_active}
        />
      </div>
    </Drawer>
  );
};

export default PlanDrawer;
